import type {
  PackingCtnInput,
  PackingCtnItemInput,
  PackingListItemDto,
} from "@hisense/shared";
import { parseIntField } from "@/lib/moneyInput";

/**
 * Pure draft <-> DTO logic for the packing-list editor. NO React, NO fetching,
 * NO i18n — every string in here is either a stored value or a document number,
 * never a user-facing label (CLAUDE.md: keep business rules IO-free so they stay
 * testable; the mirror of this file on the server is
 * `apps/api/src/services/packingLists-logic.ts`).
 *
 * Two invariants drive the whole file:
 *
 *  1. WEIGHTS ARE NOT MONEY. `pl_items.nw_kgs` / `gw_kgs` are drizzle `real`
 *     KILOGRAMS, rounded to 3 decimals server-side. They must never be routed
 *     through parseMoneyMajor / formatMoney / MONEY_SCALE, and never multiplied
 *     or divided by 100.
 *  2. `pl_items` is DENORMALISED like legacy: every row of one carton repeats
 *     that carton's nw/gw/measurement triple. So totals are summed PER CARTON
 *     (see flattenCtns server-side) and un-flattening takes Math.max per carton,
 *     never a sum (legacy convertItemsToCTNs, PackingList.js:246-249).
 */

/** One packed line inside a carton. All numerics stay as typed until submit. */
export interface CtnItemDraft {
  id?: string;
  lotNumber?: string;
  /** pi_items.id this line was packed from. Carried so the server's over-pack
   *  guard can see the line; NOT a durable FK — editing the PI re-issues every
   *  pi_items.id, so a saved id can stop resolving. */
  originalItemId?: string;
  itemNo: string;
  subRef: string;
  description: string;
  size: string;
  color: string;
  customerPo: string;
  unit: string;
  quantity: string;
}

/** One carton. The weight triple lives here, not on the lines. */
export interface CtnDraft {
  ctnNo: string;
  nwKgs: string;
  gwKgs: string;
  measurementCm: string;
  items: CtnItemDraft[];
}

/**
 * Carton sizes. Verbatim from legacy
 * `contexts/PackingListCacheContext.js:626-636`. There is no settings option
 * list for these yet (settings-logic.ts DEFAULT_SETTINGS.optionLists has no
 * `packingMeasurements`), so they ship as a client constant. These are stored
 * values rendered as `{opt}`, not translatable copy.
 */
export const MEASUREMENT_OPTIONS = [
  "(A) 15.8 x 12.5 x 10.2cm",
  "(B) 33 x 14 x 12cm",
  "(C) 33 x 14 x 24.5cm",
  "(D) 32 x 25 x 27cm",
  "(E) 45 x 35.5 x 21cm",
  "(F) 45 x 35.5 x 28cm",
];

/** Server rounds kilograms to 3 decimals; do the same so a round-trip is stable. */
const kg = (n: number) => Math.round(n * 1000) / 1000;

/**
 * Parse a typed weight in KILOGRAMS. Mirrors ProductFormPanel.parseDecimalField:
 * strips spaces, normalises thousands grouping and a locale comma-decimal, and
 * REJECTS anything that is not a clean non-negative number — it never coerces
 * junk to 0. Blank returns `{}` (neither a value nor an error); the caller
 * decides what empty means.
 */
export function parseKg(raw: string): { value?: number; invalid?: boolean } {
  let value = raw.trim().replace(/[\u00a0\u202f]/g, " ");
  if (!value) return {};
  if (/\s/.test(value)) {
    if (!/^\d{1,3}( \d{3})+([.,]\d{1,4})?$/.test(value))
      return { invalid: true };
    value = value.replaceAll(" ", "");
  }
  if (value.includes(".") && value.includes(",")) {
    if (value.lastIndexOf(",") > value.lastIndexOf(".")) {
      if (!/^\d{1,3}(\.\d{3})+,\d{1,4}$/.test(value)) return { invalid: true };
      value = value.replaceAll(".", "").replace(",", ".");
    } else {
      if (!/^\d{1,3}(,\d{3})+\.\d{1,4}$/.test(value)) return { invalid: true };
      value = value.replaceAll(",", "");
    }
  } else value = value.replace(",", ".");
  if (!/^\d+(\.\d{1,4})?$/.test(value)) return { invalid: true };
  const n = Number(value);
  return Number.isFinite(n) && n <= 1e9 ? { value: n } : { invalid: true };
}

/** Stored kilograms -> edit-field text. null stays blank; 0 stays "0". */
const kgToInput = (n: number | null | undefined) =>
  n == null ? "" : String(kg(n));

export function blankCtnItem(): CtnItemDraft {
  return {
    itemNo: "",
    subRef: "",
    description: "",
    size: "",
    color: "",
    customerPo: "",
    unit: "",
    quantity: "",
  };
}

/**
 * Next free carton number. Legacy just used `CTN${index+1}` (generateCTNNumber,
 * PackingList.js:101), which re-issues a number already in use after a carton is
 * removed — and a duplicate carton number is a hard 400 (`duplicate_ctn_no`).
 * So this takes max(existing numeric suffix) + 1 instead, falling back to the
 * carton count when nothing parses.
 */
export function nextCtnNo(ctns: CtnDraft[]): string {
  let max = 0;
  for (const c of ctns) {
    const m = /^CTN(\d+)$/i.exec(c.ctnNo.trim());
    if (m) max = Math.max(max, parseInt(m[1] ?? "0", 10));
  }
  const n = Math.max(max, ctns.length) + 1;
  return `CTN${String(n).padStart(3, "0")}`;
}

export function blankCtn(ctns: CtnDraft[]): CtnDraft {
  return {
    ctnNo: nextCtnNo(ctns),
    nwKgs: "",
    gwKgs: "",
    measurementCm: "",
    items: [],
  };
}

/**
 * Regroup the FLAT `pl_items` rows of a saved packing list back into cartons
 * (legacy convertItemsToCTNs, PackingList.js:220-250).
 *
 * Rows are keyed by ctnNo (blank -> "CTN001", as legacy did) and keep their
 * first-seen order. The carton's weights are the MAX across its rows — every
 * row repeats the same triple, so summing would multiply the weight by the line
 * count — and the measurement is the first non-empty one seen.
 */
export function ctnsFromDetail(items: PackingListItemDto[]): CtnDraft[] {
  const byCtn = new Map<string, CtnDraft>();
  for (const it of items) {
    const ctnNo = (it.ctnNo ?? "").trim() || "CTN001";
    let ctn = byCtn.get(ctnNo);
    if (!ctn) {
      ctn = {
        ctnNo,
        nwKgs: kgToInput(it.nwKgs),
        gwKgs: kgToInput(it.gwKgs),
        measurementCm: it.measurementCm ?? "",
        items: [],
      };
      byCtn.set(ctnNo, ctn);
    }
    ctn.items.push({
      id: it.id,
      lotNumber: it.lotNumber ?? "",
      originalItemId: it.originalItemId ?? undefined,
      itemNo: it.itemNo ?? "",
      subRef: it.subRef ?? "",
      description: it.description ?? "",
      size: it.size ?? "",
      color: it.color ?? "",
      customerPo: it.customerPo ?? "",
      unit: it.unit ?? "",
      quantity: String(it.quantity),
    });
  }
  return [...byCtn.values()];
}

/** An `opt()` field is `z.string().max(n).optional()` — send undefined, not "",
 *  so a cleared cell lands as NULL rather than an empty string. */
const orUndef = (s: string) => {
  const v = s.trim();
  return v === "" ? undefined : v;
};

export type CtnErrorField = "ctnNo" | "nwKgs" | "gwKgs" | "quantity";

/**
 * Drafts -> API payload. Mirrors LineItemsEditor.draftsToInput, with one
 * deliberate difference: it DROPS NOTHING. A packing list has no "blank filler
 * row" concept — a carton the clerk added is a carton they expect to save — so
 * an unusable row is reported, never silently skipped.
 *
 * `ctn` and `item` in the error are 1-based, for display.
 *
 * Blank weights mean 0 (matching legacy, and `nwKgs` is required + nonnegative
 * server-side); junk weights are rejected. Quantity must be a positive integer:
 * `packingCtnItemSchema.quantity` is `z.number().int().positive()`, so 0 and
 * blank are both invalid.
 *
 * NOT validated here (the server is the authority): duplicate carton numbers
 * (see duplicateCtnNos), empty cartons (see emptyCtnIndexes), over-packing
 * against the PI line, and the max lengths of the free-text `opt()` columns.
 */
export function draftsToInput(
  ctns: CtnDraft[],
):
  | { ctns: PackingCtnInput[] }
  | { error: { ctn: number; field: CtnErrorField; item?: number } } {
  const out: PackingCtnInput[] = [];
  for (const [i, c] of ctns.entries()) {
    const ctnNo = c.ctnNo.trim();
    if (ctnNo === "" || ctnNo.length > 20)
      return { error: { ctn: i + 1, field: "ctnNo" } };

    const nw = parseKg(c.nwKgs);
    if (nw.invalid) return { error: { ctn: i + 1, field: "nwKgs" } };
    const gw = parseKg(c.gwKgs);
    if (gw.invalid) return { error: { ctn: i + 1, field: "gwKgs" } };

    if (nw.value !== undefined && gw.value !== undefined && gw.value < nw.value)
      return { error: { ctn: i + 1, field: "gwKgs" } };
    const items: PackingCtnItemInput[] = [];
    for (const [j, it] of c.items.entries()) {
      const qty = parseIntField(it.quantity);
      if (qty.invalid || qty.value === undefined || qty.value <= 0) {
        return { error: { ctn: i + 1, field: "quantity", item: j + 1 } };
      }
      items.push({
        id: it.id,
        lotNumber: it.lotNumber ?? "",
        originalItemId: it.originalItemId || undefined,
        itemNo: orUndef(it.itemNo),
        subRef: orUndef(it.subRef),
        description: orUndef(it.description),
        size: orUndef(it.size),
        color: orUndef(it.color),
        customerPo: it.customerPo.trim(),
        unit: orUndef(it.unit),
        quantity: qty.value,
      });
    }

    out.push({
      ctnNo,
      nwKgs: nw.value === undefined ? null : kg(nw.value),
      gwKgs: gw.value === undefined ? null : kg(gw.value),
      measurementCm: orUndef(c.measurementCm),
      items,
    });
  }
  return { ctns: out };
}

/**
 * Quantity already packed, per source `pi_items.id`, across every carton. Feeds
 * the remaining-qty column of the available-items table. Lines with no
 * originalItemId (free-typed rows) have nothing to count against and are
 * ignored — exactly like the server's overpackedLines().
 */
export function packedByItemId(ctns: CtnDraft[]): Map<string, number> {
  const packed = new Map<string, number>();
  for (const c of ctns) {
    for (const it of c.items) {
      if (!it.originalItemId) continue;
      const qty = parseIntField(it.quantity).value;
      if (qty === undefined) continue;
      packed.set(it.originalItemId, (packed.get(it.originalItemId) ?? 0) + qty);
    }
  }
  return packed;
}

/**
 * Carton numbers used more than once, as typed. Client-side pre-check that
 * mirrors the server rule EXACTLY (trimmed, case-insensitive → 400
 * `duplicate_ctn_no`) so the clerk learns which carton clashes before the round
 * trip. It does not replace the server check. Blank numbers are skipped — they
 * are reported by draftsToInput as a missing ctnNo instead.
 */
export function duplicateCtnNos(ctns: CtnDraft[]): string[] {
  const seen = new Set<string>();
  const dupes = new Map<string, string>();
  for (const c of ctns) {
    const raw = c.ctnNo.trim();
    if (raw === "") continue;
    const key = raw.toUpperCase();
    if (seen.has(key)) dupes.set(key, raw);
    seen.add(key);
  }
  return [...dupes.values()];
}

/** 1-based positions of cartons with no lines. `packingCtnSchema` requires
 *  `items.min(1)`, so saving one is a 400 — surface it as packingLists.emptyCtn. */
export function emptyCtnIndexes(ctns: CtnDraft[]): number[] {
  return ctns.reduce<number[]>(
    (acc, c, i) => (c.items.length === 0 ? [...acc, i + 1] : acc),
    [],
  );
}

/* ───────────── Tạo thùng hàng loạt theo nhóm (yêu cầu v1.8 mục 7.1, 11/10/2026) ───────────── */

/** Số thùng của "CTN001" → 1; số không theo mẫu CTN + số thì null. */
export function ctnNumber(ctnNo: string): number | null {
  const m = /^CTN(\d+)$/i.exec(ctnNo.trim());
  return m ? parseInt(m[1] ?? "0", 10) : null;
}

/** 1 → "CTN001", 1000 → "CTN1000" — giữ định dạng CTN001 đang dùng (khách: "may be retained"). */
export const formatCtnNo = (n: number) => `CTN${String(n).padStart(3, "0")}`;

/** Số bắt đầu mặc định của nhóm tiếp theo = số lớn nhất đang có + 1 (nhóm 1–100 xong thì nhóm sau từ 101). */
export function nextCtnNumber(ctns: CtnDraft[]): number {
  return parseInt(nextCtnNo(ctns).slice(3), 10);
}

/** Thùng trắng chưa đụng tới (thùng mồi trang mở sẵn): không hàng, không cân, không kích thước. */
export const isUntouchedCtn = (c: CtnDraft) =>
  c.items.length === 0 && !c.nwKgs.trim() && !c.gwKgs.trim() && !c.measurementCm.trim();

export const MAX_BATCH_CTNS = 2000;

export interface CtnBatchInput {
  start: string;
  count: string;
  qtyPerCtn: string;
  nwKgs: string;
  gwKgs: string;
  measurementCm: string;
  /** Dòng hàng mẫu — chép vào MỖI thùng của nhóm với cùng số lượng. */
  item: CtnItemDraft;
  /** Số còn được đóng của dòng PI đó (sau các phiếu khác và các thùng đang có trên phiếu này). */
  remaining: number;
}

export type CtnBatchError = "start" | "count" | "qty" | "overPacked" | "nwKgs" | "gwKgs" | "weights" | "collision";

/**
 * Nhóm thùng giống nhau: N thùng đánh số liên tiếp từ `start`, mỗi thùng cùng N.W. / G.W. / kích thước và cùng một dòng hàng, cùng
 * số lượng. Từ 1, 100 thùng → CTN001…CTN100; 1.000 thùng → CTN001…CTN1000 — không trùng, không sót, xếp theo số.
 *
 * Từ chối thay vì sửa hộ: số trùng với thùng đã có, đóng vượt số còn lại của dòng PI, G.W. < N.W., số không sạch.
 */
export function buildCtnBatch(
  input: CtnBatchInput,
  existing: CtnDraft[],
): { ctns: CtnDraft[]; totalQty: number } | { error: CtnBatchError } {
  const start = parseIntField(input.start);
  if (start.invalid || start.value === undefined || start.value < 1) return { error: "start" };
  const count = parseIntField(input.count);
  if (count.invalid || count.value === undefined || count.value < 1 || count.value > MAX_BATCH_CTNS) return { error: "count" };
  const qty = parseIntField(input.qtyPerCtn);
  if (qty.invalid || qty.value === undefined || qty.value < 1) return { error: "qty" };
  const totalQty = qty.value * count.value;
  if (totalQty > input.remaining) return { error: "overPacked" };
  const nw = parseKg(input.nwKgs);
  if (nw.invalid || nw.value === undefined) return { error: "nwKgs" };
  const gw = parseKg(input.gwKgs);
  if (gw.invalid || gw.value === undefined) return { error: "gwKgs" };
  if (gw.value < nw.value) return { error: "weights" };
  const kept = existing.filter((c) => !isUntouchedCtn(c));
  const taken = new Set(kept.map((c) => c.ctnNo.trim().toUpperCase()));
  // So theo SỐ: "CTN1", "CTN01" (hệ cũ đánh `CTN${n}`) và "CTN001" là cùng một thùng số 1 — so chuỗi thì lọt (kiểm thử 11/10/2026).
  const takenNums = new Set(kept.map((c) => ctnNumber(c.ctnNo)).filter((n): n is number => n !== null));
  const out: CtnDraft[] = [];
  for (let k = 0; k < count.value; k++) {
    const ctnNo = formatCtnNo(start.value + k);
    if (taken.has(ctnNo.toUpperCase()) || takenNums.has(start.value + k)) return { error: "collision" };
    out.push({
      ctnNo,
      nwKgs: String(kg(nw.value)),
      gwKgs: String(kg(gw.value)),
      measurementCm: input.measurementCm.trim(),
      items: [{ ...input.item, id: undefined, quantity: String(qty.value) }],
    });
  }
  return { ctns: out, totalQty };
}

/** Dấu nhận dạng "thùng giống hệt" để gộp hiển thị — cùng cân, kích thước, và cùng các dòng hàng / số lượng. */
const ctnSignature = (c: CtnDraft) =>
  JSON.stringify([
    c.nwKgs.trim(), c.gwKgs.trim(), c.measurementCm.trim(),
    c.items.map((it) => [it.originalItemId ?? it.itemNo, it.lotNumber ?? "", it.quantity.trim(), it.customerPo.trim()]),
  ]);

/**
 * Các dải thùng giống hệt, số liên tiếp, đứng liền nhau (≥ `min` thùng) — để màn hình hiện "CTN001 – CTN100 · 100 thùng" thay vì
 * 100 khung. Chỉ là cách HIỂN THỊ: dữ liệu vẫn là từng thùng một như phiếu đang lưu.
 */
export function ctnRuns(ctns: CtnDraft[], min = 3): { from: number; to: number }[] {
  const runs: { from: number; to: number }[] = [];
  let i = 0;
  while (i < ctns.length) {
    let j = i;
    const sig = ctnSignature(ctns[i]!);
    const n0 = ctnNumber(ctns[i]!.ctnNo);
    while (
      j + 1 < ctns.length &&
      n0 !== null &&
      ctns[j + 1]!.items.length > 0 &&
      ctnNumber(ctns[j + 1]!.ctnNo) === n0 + (j + 1 - i) &&
      ctnSignature(ctns[j + 1]!) === sig
    )
      j++;
    if (j - i + 1 >= min && ctns[i]!.items.length > 0) runs.push({ from: i, to: j });
    i = j + 1;
  }
  return runs;
}
